import { useMemo } from 'react';

import * as hooks from './hooks';
import * as helpers from './helpers';

export const useFormatter = () => {
    const {
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        getDateForCsv,
        getDateForIms,
        getDateTimeLong,
        getDateTimeShort,
        getDateTimeForCsv,
        getDateTimeLongMs,
        getLocaleDateShort,
        parseImsBankIdDate
    } = hooks.useDateFormatter();
    const {
        formatCurrency,
        getCurrencySymbol,
        getIsCurrencySymbolAtTheLeft
    } = hooks.useCurrencyFormatter();
    const { formatNumber, delocalizeNumber } = hooks.useNumberFormatter();

    return useMemo(() => ({
        // region Currency

        /**
         * Formats amount in currency according to options
         * @param amount {Number|String} - amount to format
         * @param [options] {Object} - format params
         * @param [options.currencyCode] {String} - currency, if not passed, user's currency is used
         * @param [options.symbolAtTheLeft=true] {String} - 'true' to draw currency symbol to the left of amount
         * @param [options.precision=2] {Number} - decimal precision
         * @param [options.thSeparator=,] {String} - thousand separator
         * @param [options.decSeparator=.] {String} - decimal separator
         * @param [locale] {String} - if not passed, user's locale is used
         * @returns {String}
         */
        formatCurrency,
        /**
         * Gets currency symbol to display
         * @param [options] {String|Object} - currency code or currency options, if not passed, user's currency is used
         * @param [options.currencyCode] {String} - currency code
         * @returns {String}
         */
        getCurrencySymbol,
        /**
         * @param [options] {String|Object} - symbolAtTheLeft options
         * @param [options.symbolAtTheLeft] {String}
         * @param [options.locale] {String} - if not passed, user's locale is used
         * @return {Boolean}
         */
        getIsCurrencySymbolAtTheLeft,

        // endregion

        // region Date

        /**
         * Gets date in custom format
         * @param srcDate {*|Date|String}
         * @param [customFormat=date] {*|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        date,
        /**
         * Gets date in 'date' format e.g YYYY-MM-DD
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getDate,
        /**
         * Gets date in 'timeLong' format e.g HH:MM:SS
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getTimeLong,
        /**
         * Gets date in 'timeShort' format e.g HH:MM
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getTimeShort,
        /**
         * Gets date in 'dateTimeLong' format e.g YYYY-MM-DD HH:MM:SS
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getDateTimeLong,
        /**
         * Gets date in 'dateTimeShort' format e.g YYYY-MM-DD HH:MM
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getDateTimeShort,
        /**
         * Gets date in 'dateTimeLongMs' format e.g YYYY-MM-DD HH:MM:SS.sss
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getDateTimeLongMs,
        /**
         * Gets date in '12 Apr 2018' format
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getLocaleDateShort,
        /**
         * Formats date for export to CSV in 'YYYY-MM-DD' format
         * @param srcDate {*|Date|String}
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        getDateForCsv,
        /**
         * Formats date and time for export to CSV in 'YYYY-MM-DD HH:MM' format
         * @param srcDate {*|Date|String}
         * @return {String}
         */
        getDateTimeForCsv,
        /**
         * Parse date string received from IMS
         * IMS sends us date in non-standard format ('YYYY-MM-DD HH:MM:SS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @param srcDate {String}
         * @returns {Date}
         */
        parseImsDate,
        /**
         * Get date in format 'YYYY-MM-DD HH:MM:SS.sss' in GMT to be sent to IMS (through OGW)
         * @param srcDate {Date}
         * @return {String}
         */
        getDateForIms,
        /**
         * Parse date string received from getUrlRequest
         * IMS sends us date in non-standard format ('YYYY-MM-DD+HH%3AMM%3ASS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @memberOf Playtech.API.formatter
         * @function
         * @param scrDate {String}
         * @returns {Date}
         */
        parseImsBankIdDate,

        // endregion

        // region Number

        /**
         * Formats number according to options
         * @param number {Number|String} - number to format
         * @param [options] {Object} - format params
         * @param [options.precision=null] {Number} - decimal precision
         * @param [options.thSeparator=,] {String} - thousand separator
         * @param [options.decSeparator=.] {String} - decimal separator
         * @param [locale] {String} - if not passed, user's locale is used
         * @return {String}
         */
        formatNumber,
        /**
         * Convert string, that represents localized number, into number if possible
         * @param str {String} - source string
         * @param [options] {Object} - source format params
         * @param [options.thSeparator=,] {String} - thousand separator
         * @param [options.decSeparator=.] {String} - decimal separator
         * @return {*|Number|String}
         */
        delocalizeNumber,

        // endregion

        // region Other

        /**
         * Get duration in 'HH:MM:SS' format without timezone offset
         * @param duration {Date|String|Number}
         * @returns {String}
         */
        getDurationTimeLong: helpers.getDurationTimeLong,
        /**
         * Get duration in 'HH:MM' format without timezone offset
         * @param duration {Date|String|Number}
         * @returns {String}
         */
        getDurationTimeShort: helpers.getDurationTimeShort,
        /**
         * Converts seconds to HH:MM:SS or MM:SS
         * @param seconds {String|Number}
         * @returns {String}
         */
        formatTimeFromSeconds: helpers.formatTimeFromSeconds

        // endregion
    }), [
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        formatNumber,
        getDateForCsv,
        getDateForIms,
        formatCurrency,
        getDateTimeLong,
        getDateTimeShort,
        delocalizeNumber,
        getDateTimeForCsv,
        getCurrencySymbol,
        getDateTimeLongMs,
        getLocaleDateShort,
        parseImsBankIdDate,
        getIsCurrencySymbolAtTheLeft
    ]);
};

